import { useMemo } from 'react';
import { ArrowRightAlt } from '@mui/icons-material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Button, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Action, useScheduledActionsDoneTodayQuery } from '../../../../api/gql';
import { ClaimIcon, InvoiceIcon } from '../../../../components/icons/CustomIcons';
import { ActionIcon } from '../../../../components/invoice/ScheduledActionIcon';
import { ActionName } from '../../../../components/invoice/ScheduledActionName';
import { BooleanIndicator } from '../../../../components/table/BooleanIndicator';
import { renderCellOrDefault, renderShowCellOrDefault } from '../../../../components/table/renderCellOrDefault';
import { Table } from '../../../../components/table/Table';
import { dateGridColDef } from '../../../../components/table/utils/columnHeader';
import { actionToTransMap } from '../../../../utils/maps';

export const DoneScheduledActionsTable = () => {
  const { t } = useTranslation('page-admin-my-tasks');
  const { isLoading, data } = useScheduledActionsDoneTodayQuery();

  const rows = useMemo(
    () =>
      data?.scheduledActionsDoneToday
        .sort((a, b) => {
          if (a.scheduledAt < b.scheduledAt) return -1;
          return 1;
        })
        .map((task) => {
          return {
            id: task.id,
            plannedAt: task.scheduledAt,
            doneAt: task.doneAt,
            action: task.action,
            performs: task.action,
            success: task.success,
            vendor: task.vendorName,
            customer: task.customerName,
            invoiceNumber: task.invoiceNumber,
            invoiceId: task.invoiceId,
            isClaim: task.invoiceClaim,
          };
        }) ?? [],
    [data?.scheduledActionsDoneToday],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'plannedAt',
        headerName: t('table.plannedAt') as string,
        minWidth: 130,
        ...dateGridColDef,
      },
      {
        field: 'doneAt',
        headerName: t('table.doneAt') as string,
        minWidth: 130,
        ...dateGridColDef,
      },
      {
        field: 'action',
        headerName: t('table.action') as string,
        minWidth: 130,
        valueGetter: (params) => {
          return t(actionToTransMap[params.value as Action], {
            ns: 'translation',
          });
        },
        renderCell: (params) => {
          return (
            <Stack direction={'row'} gap={1} alignItems={'center'}>
              <ActionIcon action={params.row.action} color={'action'} fontSize={'small'} />
              <ActionName action={params.row.action} fontSize={'small'} />
            </Stack>
          );
        },
      },

      {
        field: 'performs',
        headerName: t('table.performs') as string,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) =>
          params.value === Action.Call ? (
            <SupportAgentIcon color={'action'} />
          ) : (
            <SmartToyOutlinedIcon color={'action'} />
          ),
      },
      {
        field: 'isClaim',
        headerName: t('table.remindingObject') as string,
        minWidth: 150,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) =>
          params.value ? (
            <Stack direction={'row'} gap={1} alignItems={'center'}>
              <ClaimIcon color={'action'} />
              <Typography variant={'caption'} color={'action'}>
                {t('claim')}
              </Typography>
            </Stack>
          ) : (
            <Stack direction={'row'} gap={1} alignItems={'center'}>
              <InvoiceIcon color={'action'} />
              <Typography variant={'caption'} color={'action'}>
                {t('invoice')}
              </Typography>
            </Stack>
          ),
      },
      {
        field: 'invoiceNumber',
        headerName: t('table.invoice') as string,
        minWidth: 130,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'success',
        headerName: t('table.success') as string,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => <BooleanIndicator value={params.value} />,
      },
      {
        field: 'vendor',
        headerName: t('table.vendor') as string,
        flex: 1,
        minWidth: 120,
        renderCell: (params) => renderShowCellOrDefault(!params.row.isClaim, params),
      },
      {
        field: 'customer',
        headerName: t('table.customer') as string,
        flex: 1,
        minWidth: 150,
        renderCell: renderCellOrDefault,
      },
      {
        field: 'invoiceId',
        headerName: '',
        filterable: false,
        sortable: false,
        disableColumnMenu: true,
        flex: 1,
        align: 'right',
        minWidth: 360,
        renderCell: (params) => (
          <Stack direction={'row'}>
            {params.row.isClaim ? (
              <Button component={Link} to={`/admin/completed-scheduled-actions/claim/${params.value}`}>
                {t('claimDetail')}
              </Button>
            ) : (
              <Button component={Link} to={`/admin/completed-scheduled-actions/invoice/${params.value}`}>
                {t('invoiceDetail')}
              </Button>
            )}
            <Button
              variant={'contained'}
              component={Link}
              to={`/admin/completed-scheduled-actions/scheduled-action/${params.id}`}
              endIcon={<ArrowRightAlt />}
            >
              {t('taskDetail')}
            </Button>
          </Stack>
        ),
      },
    ],
    [t],
  );

  return (
    <Table
      id={'admin-done-scheduled-actions'}
      loading={isLoading}
      autoHeight={true}
      rows={rows}
      columns={columns}
      initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
    />
  );
};
